import { msg } from '@/services/isomorphic/I18NService';
import LoggerService from '@/services/isomorphic/LoggerService';
import S from './styles.base.module.scss';
import { product_tiles_bestFor } from "@/lang/__generated__/ahnu/product_tiles_bestFor";

// BestForText interface.
export interface IBestForTextProps {
  /**
   * The surfaces that the shoe is best suited for.
   */
  shoeSurface?: Array<string>;
}

// The mapping of transformed shoe surface keys to their proper display names.
export const shoeSurfaceKeyMap = {
  comfort: 'Comfort',
  trail: 'Trail',
  hiking: 'Hiking',
  hike: 'Hike',
  water: 'Water',
  race: 'Racing',
  road: 'Road',
  competition: 'Competition',
  walking: 'Walking',
  recovery: 'Recovery',
  lifestyle: 'Lifestyle',
  work: 'Professional Use',
  racing: 'Race',
  travel: 'Travel',
  camping: 'Camping',
  every_day_running: 'Everyday Run',
  everyday_running: 'Everyday Run',
  every_day_wear: 'Everyday Wear',
  everyday_wear: 'Everyday Wear',
  festival: 'Festival',
  trail_running: 'Trail Running',
  all_weather_city_wear: 'All Weather City Wear',
  all_day_wear: 'All-Day Wear',
  day_hikes: 'Day Hikes',
  gym_training: 'Training',
  all_terrain_running: 'All Terrain Running',
  light_hikes: 'Light Hikes',
  multi_day_hikes: 'Multi Day Hikes',
  getting_around_town: 'Getting Around Town',
  water_activities: 'Water Activities',
  all_weather_hiking: 'All Weather Hiking',
  relaxing_at_the_campground: 'Relaxing at the Campground',
  fast_hikes: 'Fast Hikes',
  outdoor_adventures: 'Outdoor Adventures',
  cold_weather: 'Cold Weather',
  post_adventure_wear: 'Post-Adventure Wear',
  casual_hikes: 'Casual Hikes',
  backpacking: 'Backpacking',
  all_purpose_trail: 'All Purpose Trail',
  technical_hikes: 'Technical Hikes',
  water_sports: 'Water Sports'
};

/**
 * A text component to display the "Best for" surfaces for the product.
 *
 * Currently, we are unable to retrieve the shoeSurface display name values from SFCC.
 * So, for now, we are reading the available surfaces key values from the productModel,
 * replacing spaces and dashes with underscores, and converting them to lowercase
 * and then mapping them to the shoeSurfaceMap object above to show the proper
 * display name(s).
 *
 * If a given shoeSurface key has no mapping in the product config,
 * it will be skipped and not shown in the output.
 *
 * @param shoeSurface - The shoe surfaces applied to the product.
 * @returns JSX.Element | null.
 *
 * Example: "Best for: Hiking, Trail, Training".
 */
export const BestForText = ({ shoeSurface }: IBestForTextProps) => {
  if (!shoeSurface || shoeSurface.length === 0) return null;

  const items = shoeSurface
    .map((surface) => {
      const key = surface
        .toLowerCase()
        .replace(/[\s-]+/g, '_') as keyof typeof shoeSurfaceKeyMap;
      const label = shoeSurfaceKeyMap[key];

      // If the label is not found in the map, log a warning and skip it.
      if (!label) {
        LoggerService.warn(
          `Surface ${surface} not found for key ${key} in shoeSurfaceKeyMap. Skipping it.`
        );
        return null;
      }

      return (
        <span className={S.bestForTextItem} key={key}>
          {label}
        </span>
      );
    })
    .filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div className={S.infoRow}>
      <div className={S.bestForText}>
        {msg(product_tiles_bestFor)}
        {items}
      </div>
    </div>
  );
};
